import { useCallback, useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import {
  Box,
  Button,
  Flex,
  HStack,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
} from "@chakra-ui/react";
import { Check, CloudRain, Info, Zap } from "lucide-react";
import { useStores } from "stores/setup/use-store";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import {
  LIGHTNING_RADIUS_MAX_KM,
  LIGHTNING_RADIUS_MIN_KM,
  LIGHTNING_RADIUS_STEP_KM,
} from "components/SensorAlertSection/lightning/lightning-constants";
import { isLightningAllowedForOrigin } from "util/lightningGate";

/**
 * Floating control panel mounted on top of the forecast-charts map while the
 * user is in sensor-selection mode. Mirrors the spec 042 panel from the new
 * app (`apps/i4cast-web/.../sensor-selection-controls.tsx`):
 *  - Always-visible Chuva/Raio tab strip
 *  - Per-tab body (rain count + warning OR lightning info + slider)
 *  - Two-row footer (Voltar + optional Limpar / Concluir configuração)
 *
 * Visual identity stays in the legacy Chakra/`newBlue` palette — no new design
 * tokens. Translations come from the `oceanWeatherIntelligence.sensorAlert`
 * namespace already used by the drawer.
 */
function SensorSelectionControls() {
  const {
    dataStores: { forecastChartPageStore, insightAlertStore },
  } = useStores();

  const history = useHistory();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const origin = queryParams.get("origin") as string | null;
  const controlRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.sensorAlert",
  });

  const selectedCount = forecastChartPageStore.selectedSensorIds.length;
  const activeTab = forecastChartPageStore.sensorSelectionTab;
  const lightningSlice = insightAlertStore.lightningSelection;

  // Gate de lightning por origem: a criação de evento via insight-manager não
  // expõe o tab Raio no gateway. Demais origens (alertas, edições, forecast)
  // mantêm o lightning disponível.
  const lightningAllowed = isLightningAllowedForOrigin(origin);
  const visibleSelectionTabs = lightningAllowed
    ? (["rain", "lightning"] as const)
    : (["rain"] as const);

  // Tab efetivamente exibido. Quando a origem bloqueia lightning, força "rain"
  // de forma síncrona — sem isso, o body do painel pode renderizar conteúdo
  // de lightning entre o render inicial e o effect que normaliza o store.
  const effectiveTab = lightningAllowed ? activeTab : "rain";

  // Disable map click/scroll propagation on the control panel
  useEffect(() => {
    if (controlRef.current) {
      L.DomEvent.disableClickPropagation(controlRef.current);
      L.DomEvent.disableScrollPropagation(controlRef.current);
    }
  }, []);

  // Defesa-em-profundidade: se o tab ativo no store for "lightning" mas a
  // origem está bloqueada (insight-manager), volta para "rain". Necessário
  // para que `handleEditSelection` (que persiste o tab ativo no store antes
  // da navegação) não consiga prefixar o gateway em "lightning".
  useEffect(() => {
    if (!lightningAllowed && activeTab === "lightning") {
      forecastChartPageStore.setSensorSelectionTab("rain");
    }
  }, [lightningAllowed, activeTab, forecastChartPageStore]);

  // Add drawing-mode class to adjust leaflet controls position
  useEffect(() => {
    const leafletTopElement = document.querySelector(".leaflet-top");
    if (leafletTopElement) {
      if (forecastChartPageStore.isSensorSelectionMode) {
        leafletTopElement.classList.add("drawing-mode");
      } else {
        leafletTopElement.classList.remove("drawing-mode");
      }
    }
    return () => {
      if (leafletTopElement) {
        leafletTopElement.classList.remove("drawing-mode");
      }
    };
  }, [forecastChartPageStore.isSensorSelectionMode]);

  const navigateBack = useCallback(() => {
    if (origin === "event-edit") {
      const eventInsightId = queryParams.get("event_insight_id");
      if (!eventInsightId) {
        history.push("/insight-manager?tab=1");
        return;
      }
      history.push(
        `/insight-manager?tab=1&open_edit_drawer=true&event_insight_id=${eventInsightId}`,
      );
    } else if (origin === "alert-edit") {
      const alertId = queryParams.get("alert_id");
      if (alertId) {
        history.push(`/insight-alerts?alert_id=${alertId}`);
      } else {
        history.push("/insight-alerts");
      }
    } else if (origin === "insight-manager") {
      history.push("/insight-manager?tab=1&open_create_drawer=true");
    } else if (origin === "insight-alerts") {
      history.push("/insight-alerts?open_create_drawer=true");
    } else if (
      origin === "forecast-charts" ||
      origin === "event-create" ||
      origin === "event-create-suggestion"
    ) {
      history.push("/forecast-charts?open_create_drawer=true");
    } else {
      history.goBack();
    }
  }, [origin, history, queryParams]);

  const onBack = useCallback(() => {
    // Spec 043 §FR-006a — restore both rain selection and lightning slice to
    // the snapshot taken on entry. Without this any in-session changes (placed
    // point, dragged radius, sensor toggles) would leak into the drawer.
    const previousIds = forecastChartPageStore.preSensorSelectionIds;
    insightAlertStore.setSelectedSensorIds([...previousIds]);
    insightAlertStore.setLightningSelection(
      forecastChartPageStore.preSensorSelectionLightning,
    );
    forecastChartPageStore.clearSensorSelection();
    navigateBack();
  }, [forecastChartPageStore, insightAlertStore, navigateBack]);

  const onConcluir = useCallback(() => {
    // Commit the transient sensor selection. Lightning is already canonical
    // (mutated in place by SensorSelectionMapClickHandler / slider), so the
    // drawer will pick it up via insightAlertStore.lightningSelection on
    // remount.
    insightAlertStore.setSelectedSensorIds([
      ...forecastChartPageStore.selectedSensorIds,
    ]);
    forecastChartPageStore.clearSensorSelection();
    navigateBack();
  }, [forecastChartPageStore, insightAlertStore, navigateBack]);

  const onClearLightning = useCallback(() => {
    insightAlertStore.setLightningSelection(null);
  }, [insightAlertStore]);

  const onRadiusChange = useCallback(
    (km: number) => {
      if (!lightningSlice) return;
      insightAlertStore.setLightningSelection({
        ...lightningSlice,
        radiusKm: km,
      });
    },
    [insightAlertStore, lightningSlice],
  );

  const concluirDisabled =
    effectiveTab === "lightning"
      ? !lightningSlice && selectedCount === 0
      : selectedCount === 0 && !lightningSlice;

  return (
    <Box
      ref={controlRef}
      position="absolute"
      top="7px"
      left="20px"
      zIndex={1000}
      background="white"
      padding="16px"
      borderRadius="8px"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
      width="380px"
      maxWidth="380px"
    >
      <Flex direction="column" gap={4}>
        {/* Title block — always visible (mirrors the Aurora multi-type card). */}
        <Box>
          <Text
            fontSize="16px"
            fontWeight="600"
            color="#313F4D"
            lineHeight="1.4"
            textAlign="center"
          >
            {t("sensorSelectionTitleMulti")}
          </Text>
          <Text
            fontSize="14px"
            color="#4A6075"
            lineHeight="1.5"
            mt={2}
          >
            {t("sensorSelectionSubtitleMulti")}
          </Text>
        </Box>

        {/* Tab strip — always shows both Chuva and Raio (mirrors the Aurora
            "map" mode of <SensorAlertTabs>). Configured tabs get a green check
            mark beside the label. We don't use Chakra's <Tabs> here because the
            `as={HStack}` workaround needed to inline icon+label was collapsing
            the second tab; this manual implementation keeps both tabs visible
            and equally weighted regardless of state. */}
        <HStack
          spacing={0}
          borderBottom="1px solid"
          borderBottomColor="gray.200"
        >
          {visibleSelectionTabs.map((tab) => {
            const isActive = effectiveTab === tab;
            const configured =
              tab === "rain" ? selectedCount > 0 : !!lightningSlice;
            return (
              <Box
                key={tab}
                as="button"
                type="button"
                onClick={() =>
                  forecastChartPageStore.setSensorSelectionTab(tab)
                }
                flex={1}
                px={3}
                py={2}
                bg="transparent"
                borderBottom="2px solid"
                borderBottomColor={isActive ? "#0884CC" : "transparent"}
                color={isActive ? "#0884CC" : "#4A6075"}
                fontWeight={isActive ? 600 : 500}
                fontSize="14px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1.5}
                cursor="pointer"
                _hover={{ color: "#0884CC" }}
                transition="color 120ms, border-color 120ms"
              >
                <Box
                  as={(tab === "lightning" ? Zap : CloudRain) as any}
                  boxSize="14px"
                />
                <Box>
                  {tab === "lightning"
                    ? t("sensorTypeLightningTab")
                    : t("sensorTypeRainTab")}
                </Box>
                {configured && (
                  <Box
                    as={Check as any}
                    boxSize="14px"
                    color="#16A34A"
                    aria-hidden
                  />
                )}
              </Box>
            );
          })}
        </HStack>

        {/* Per-tab body */}
        {effectiveTab === "rain" ? (
          <>
            <Box bg="gray.50" py={2} px={3} borderRadius="md">
              <Text fontSize="sm" fontWeight="500" color="#313F4D">
                {t("selectedSensors", { count: selectedCount })}
              </Text>
            </Box>
            {selectedCount === 1 && (
              <Flex
                bg="#FFFBF1"
                border="1px solid #FCE499"
                borderRadius="8px"
                p="12px"
                gap="10px"
                align="center"
              >
                <Box
                  as={Info as any}
                  boxSize="16px"
                  color="#987405"
                  flexShrink={0}
                />
                <Text fontSize="14px" color="#987405" lineHeight="20px">
                  {t("singleSensorWarning")}
                </Text>
              </Flex>
            )}
          </>
        ) : (
          <>
            {/* Info banner — call-to-action when no point placed, info when placed */}
            <Flex
              bg="#DBF1FE"
              border="1px solid #0884CC"
              borderRadius="8px"
              p="12px"
              gap="10px"
              align="center"
            >
              <Box
                as={Info as any}
                boxSize="16px"
                color="#086AA9"
                flexShrink={0}
              />
              <Text fontSize="14px" color="#086AA9" lineHeight="20px">
                {lightningSlice
                  ? t("lightningSelectionInfo")
                  : t("lightningSelectionCallToAction")}
              </Text>
            </Flex>

            {lightningSlice && (
              <Box>
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="#313F4D"
                  mb={1}
                >
                  {t("lightningRadiusLabel")}
                </Text>
                <Text fontSize="13px" color="#4A6075" mb={3}>
                  {t("lightningRadiusDescription")}
                </Text>
                <Slider
                  aria-label={t("lightningRadiusLabel") ?? undefined}
                  min={LIGHTNING_RADIUS_MIN_KM}
                  max={LIGHTNING_RADIUS_MAX_KM}
                  step={LIGHTNING_RADIUS_STEP_KM}
                  value={lightningSlice.radiusKm}
                  onChange={onRadiusChange}
                  colorScheme="blue"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb boxSize={5} />
                </Slider>
                <Flex justify="space-between" mt={1}>
                  <Text fontSize="11px" color="#4A6075" fontWeight="500">
                    {LIGHTNING_RADIUS_MIN_KM} km
                  </Text>
                  <Text
                    fontSize="11px"
                    color="#086AA9"
                    fontWeight="600"
                  >
                    {t("lightningRadiusValue", {
                      km: lightningSlice.radiusKm,
                    })}
                  </Text>
                  <Text fontSize="11px" color="#4A6075" fontWeight="500">
                    {LIGHTNING_RADIUS_MAX_KM} km
                  </Text>
                </Flex>
              </Box>
            )}
          </>
        )}

        {/* Footer — two-row layout matching the new app */}
        <Flex direction="column" gap={2}>
          <Flex gap={3} align="center">
            <Button
              flex={1}
              color="newBlue.500"
              variant="outline"
              size="sm"
              onClick={onBack}
            >
              {t("backToEvent")}
            </Button>
            {effectiveTab === "lightning" && lightningSlice && (
              <Button
                flex={1}
                variant="ghost"
                colorScheme="blue"
                size="sm"
                onClick={onClearLightning}
              >
                {t("clearLightningSelection")}
              </Button>
            )}
          </Flex>
          <Button
            layerStyle="ocean"
            _hover={{ layerStyle: "ocean" }}
            size="sm"
            onClick={onConcluir}
            isDisabled={concluirDisabled}
            w="100%"
          >
            {t("concludeConfiguration")}
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}

export default observer(SensorSelectionControls);
